import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/data-view/client/x-data-view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input';
import '@adornis/popover/x-dropdown-selection.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { emailRegexCheck, passwordRegexCheck } from '@adornis/users/db/validators.js';
import { User, UserPermission } from '@dla/db/User.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../components/dla-collab-editor.js';
import '../components/dla-permission-editor.js';
import { DlaTranslationDict } from '../translations.js';

@Entity()
class NewUserInput extends AdornisEntity {
  static override _class = 'NewUserInput';

  @Field(type => String)
  username: Maybe<string>;

  @Field(type => String)
  email: Maybe<string>;

  @Field(type => String)
  password: Maybe<string>;
}

@customElement('dla-user-management')
export class DLAUserManagement extends ChemistryLitElement {
  @state() _newUserInput = new FormController(this, new NewUserInput({}));

  private _translator = new TranslationController<DlaTranslationDict>(this);

  private async _showUserDialog(error?: string) {
    const res = await XDialog.showPopup<boolean>({
      content: resolve => {
        return html`<x-flex padding="md" space="md">
          <x-input
            placeholder=${this._translator.translate('field', 'username')}
            ${this._newUserInput.field('username')}
          ></x-input>
          <x-input
            placeholder=${this._translator.translate('field', 'email')}
            ${this._newUserInput.field('email')}
          ></x-input>
          <x-input
            placeholder=${this._translator.translate('field', 'password')}
            type="password"
            ${this._newUserInput.field('password')}
          ></x-input>
          <x-text> ${this._translator.translate('passwordInfo')} </x-text>
          ${error ? html`<x-text bold ${css({ color: 'red' })}> ${error} </x-text>` : ''}
          <x-button @click=${() => resolve(true)}> ${this._translator.translate('button', 'add')} </x-button>
        </x-flex>`;
      },
    });

    if (res) {
      try {
        if (!passwordRegexCheck(this._newUserInput.document?.password ?? '')) {
          throw new Error(this._translator.translate('error', 'passwordCriteria'));
        }

        if (!emailRegexCheck(this._newUserInput.document?.email ?? '')) {
          throw new Error(this._translator.translate('error', 'emailCriteria'));
        }

        if (!this._newUserInput.document?.username || !this._newUserInput.document?.email) {
          throw new Error(this._translator.translate('error', 'usernamePasswordRequired'));
        }

        await User.registerUser({
          username: this._newUserInput.document?.username ?? '',
          email: this._newUserInput.document?.email ?? '',
          password: this._newUserInput.document?.password ?? '',
        });
      } catch (e: any) {
        console.log(e);
        this._showUserDialog(e.message);
      } finally {
        this._newUserInput = new FormController(this, new NewUserInput({}));
      }
    }
  }

  override render() {
    return permissionGuard(
      UserPermission.UserManagement,
      () => html`<x-flex padding="lg" space="md">
        <x-data-view
          .options=${{
            class: User,
            customColumns: [
              { fieldName: 'username', columnName: 'Username', columnWidth: 'max-content' },
              { fieldName: 'email', columnName: 'E-Mail' },
              {
                customID: 'action',
                columnWidth: '150px',
                columnName: '',
                getFieldMapping: e => {
                  return html`<x-button
                    tone="error"
                    @click=${async () => {
                      XDialog.guard(
                        async () => {
                          await e.remove();
                        },
                        {
                          hasToConfirm: true,
                          confirmText: this._translator.translate('button', 'confirmUserDelete'),
                        },
                      );
                    }}
                  >
                    ${this._translator.translate('button', 'delete')}
                  </x-button>`;
                },
              },
            ],
          }}
        >
        </x-data-view>
        <x-button
          @click=${async () => {
            await this._showUserDialog();
          }}
        >
          ${this._translator.translate('button', 'add')}
        </x-button>
      </x-flex>`,
      {
        context: ANY_CONTEXT,
        forbiddenTemplate: html`<x-flex padding="lg"
          ><x-text> ${this._translator.translate('accessDenied')} </x-text></x-flex
        >`,
      },
    );
  }
}
